import React, { useEffect, useState, useCallback } from 'react';
import RfcFacil from 'rfc-facil';
import { useDispatch } from 'react-redux';

import { isFormValid } from './form.validations';
import PersonalInfoComponent from '../../components/form/form.component';
import { useTracking } from '../../../../../shared/utilities/tagManager/tagManager.hook';
import {
  TrackingAction,
  TrackingCategory,
  TrackingEvent,
} from '../../../../tracking/tracking.constants';

import useSelector from '../../../../redux/useSelector';
import { DataPersonalInfoState } from '../../redux/personalInfo.state';
import { SetProperty, SetReduxState } from '../../redux/personalInfo.actions';

import useLocalStorage from '../../hooks/useLocalStorage.hook';
import getFirstFieldError from '../../../../utils/showErrorField';
import SavePerson from '../../requests/savePersonInfo/savePersonInfo';
import { PersonPageProps, EnumNamesInputs } from '../../types/form.types';
import getQueryParams from '../../../../../shared/utilities/queryParams/getQueryParams';
import { navigateWithParams } from '../../../../../shared/utilities/queryParams/navigate';

const PersonalInfoContainer: React.FC<PersonPageProps> = (
  props: PersonPageProps,
) => {
  const dispatch = useDispatch();
  const { trackPageLoad, trackEvent } = useTracking();
  const { storedValue, setStoreValue } = useLocalStorage({
    key: 'chsPersonalPageValues',
  });
  const personalInfoValues = useSelector(state => state.personalInfo.person);
  const isLoading = useSelector(state => state.personalInfo.isLoading);

  const [errors, setErrors] = useState(
    isFormValid({
      ...personalInfoValues,
    }),
  );
  const [isSubmitted, setSubmitted] = useState(false);

  const constructFieldsComplete = useCallback((): boolean => {
    const dateElements = personalInfoValues.birthDate
      .split('/')
      .map((v: string) => parseInt(v));
    return (
      personalInfoValues.firstName !== '' &&
      personalInfoValues.firstLastname !== '' &&
      personalInfoValues.secondLastname !== '' &&
      dateElements.length > 2
    );
  }, [
    personalInfoValues.firstName,
    personalInfoValues.firstLastname,
    personalInfoValues.secondLastname,
    personalInfoValues.birthDate,
  ]);

  useEffect(() => {
    const queryParams = getQueryParams();
    trackPageLoad({
      page: {
        category: TrackingCategory.PERSON_PAGE,
        action: TrackingAction.PERSON_PAGE_LOAD,
      },
    });
    const leadId = queryParams.get('session');
    if (leadId) localStorage.setItem('lead_id', leadId);
  }, [trackPageLoad]);

  useEffect(() => {
    const data = storedValue;
    if (typeof storedValue === 'object') {
      dispatch(SetReduxState(data as DataPersonalInfoState));
    }
  }, [storedValue, dispatch]);

  useEffect(() => {
    const formErrors = isFormValid({
      ...personalInfoValues,
    });

    setErrors(formErrors);
  }, [personalInfoValues, dispatch]);

  const handleChangeInput = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    event.persist();
    dispatch(
      SetProperty({
        name: event.target.name,
        value: event.target.value,
      }),
    );
  };

  const handleNamesChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    dispatch(
      SetProperty({
        name: EnumNamesInputs.NAMES,
        value: event.target.value,
      }),
    );
    const words = event.target.value.split(' ');
    dispatch(
      SetProperty({
        name: EnumNamesInputs.FIRST_NAME,
        value: words && words.length ? words[0] : '',
      }),
    );
    words.splice(0, 1);
    dispatch(
      SetProperty({
        name: EnumNamesInputs.SECOND_NAME,
        value: words && words.length ? words.join(' ') : '',
      }),
    );
  };

  const handleSubmit = (event: React.ChangeEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    setSubmitted(true);
    if (!errors) {
      SavePerson(() => {
        setStoreValue({
          person: personalInfoValues,
        });
        trackEvent({
          event: {
            name: TrackingEvent.CREDIT_HISTORY,
            category: TrackingCategory.PERSON_PAGE,
            action: TrackingAction.PERSON_PAGE_COMPLETE,
          },
        });
        navigateWithParams(props.goTo);
      });
    } else {
      const elem = getFirstFieldError(errors);
      if (elem) {
        elem.scrollIntoView();
        window.scrollTo(0, window.scrollY - 50);
      }
    }
  };

  useEffect(() => {
    const dateElements = personalInfoValues.birthDate
      .split('/')
      .map((v: string) => parseInt(v));

    const generateRFC = (): string => {
      const rfcAPIData = {
        name: `${personalInfoValues.firstName} ${personalInfoValues.secondName}`,
        firstLastName: personalInfoValues.firstLastname,
        secondLastName: personalInfoValues.secondLastname,
        day: dateElements[0],
        month: dateElements[1],
        year: dateElements[2],
      };
      return RfcFacil.forNaturalPerson(rfcAPIData);
    };
    try {
      if (constructFieldsComplete()) {
        dispatch(
          SetProperty({
            name: 'rfc',
            value: generateRFC(),
          }),
        );
      }
    } catch (error) {
      console.error(error);
    }
  }, [
    personalInfoValues.firstName,
    personalInfoValues.secondName,
    personalInfoValues.firstLastname,
    personalInfoValues.secondLastname,
    personalInfoValues.birthDate,
    constructFieldsComplete,
    dispatch,
  ]);

  useEffect(() => {
    const queryParams = getQueryParams();
    const emailUtm = queryParams.get('email');
    if (emailUtm) {
      dispatch(
        SetProperty({
          name: EnumNamesInputs.EMAIL,
          value: emailUtm,
        }),
      );
    }
  }, [dispatch]);

  useEffect(() => {
    const queryParams = getQueryParams();
    const phonelUtm = queryParams.get('phone');
    if (phonelUtm) {
      dispatch(
        SetProperty({
          name: EnumNamesInputs.PHONE_NUMBER,
          value: phonelUtm,
        }),
      );
    }
  }, [dispatch]);

  return (
    <PersonalInfoComponent
      {...personalInfoValues}
      isLoading={isLoading}
      errors={isSubmitted ? errors : undefined}
      handleSubmit={handleSubmit}
      handleChangeInput={handleChangeInput}
      handleNamesChange={handleNamesChange}
    />
  );
};

export default PersonalInfoContainer;
